import React, { useCallback, useMemo, useState } from 'react';
import { Button } from '@/design-system/button';
import {
  FileUploadDropzone,
  FileUploadItem,
  FileUploadList,
} from '@/design-system/file-upload';
import { Label } from '@/design-system/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/design-system/select';
import {
  COD_PROOF_TYPES,
  COD_PROOF_TYPE_LABELS,
  type CodConfirmationProof,
  type CodProofType,
  uploadCodProof,
} from '@/services/api/codOrders';

export const PROOF_ACCEPTED = '.pdf,.png,.jpg,.jpeg,.webp';
export const PROOF_MAX_BYTES = 5 * 1024 * 1024;
/** Matches the server's per-request cap. */
const MAX_ATTACHMENTS = 10;

/**
 * One attachment being prepared: WHAT it is, then the file itself.
 *
 * A pair, in that order, because the type is the part a reader of the leg
 * cannot recover later — a file called `IMG_2831.jpg` says nothing about
 * whether it is the transfer slip or the Berita Acara. Asking for it beside the
 * file (rather than after it) is what stops every attachment ending up labelled
 * the same.
 */
interface ProofRow {
  type: CodProofType;
  file: File | null;
  /**
   * Set once THIS file has actually reached the bucket.
   *
   * The row used to have no upload state at all, so a freshly picked file
   * rendered with a green tick and the word "Completed" — before a single byte
   * had left the browser. Nothing uploads until the form is submitted. On a
   * screen whose whole job is attaching evidence for money, "Completed" has to
   * mean the evidence is stored, not that a file name is on screen.
   */
  uploadedUrl?: string;
  /** The upload's own failure, kept on the row that failed. */
  error?: string;
}

const emptyRow = (): ProofRow => ({ type: 'bukti_transfer', file: null });

/**
 * The attachment state for one form: what is already stored, the rows being
 * filled in, and the upload that turns the second into the first.
 *
 * One hook for four surfaces — confirm, correction, bulk and the add-proof
 * modal — because they differ only in what they start with and whether the
 * stored list can be pruned. Four copies of "upload each file then collect the
 * urls" is four places for the error handling to drift.
 *
 * `existing` starts non-empty only on a correction, where the endpoint takes
 * the list the leg should END UP WITH: removing one there is dropping it from
 * this array and submitting the rest.
 */
export const useProofPicker = (initial: CodConfirmationProof[] = []) => {
  const [existing, setExisting] = useState<CodConfirmationProof[]>(initial);
  const [rows, setRows] = useState<ProofRow[]>([emptyRow()]);
  const [uploading, setUploading] = useState(false);

  const filled = rows.filter((row) => row.file !== null);

  const addRow = useCallback(() => {
    setRows((prev) =>
      prev.length + existing.length >= MAX_ATTACHMENTS
        ? prev
        : [...prev, emptyRow()]
    );
  }, [existing.length]);

  const removeRow = useCallback((index: number) => {
    // Never below one row: an empty form still needs somewhere to start.
    setRows((prev) =>
      prev.length === 1 ? [emptyRow()] : prev.filter((_, i) => i !== index)
    );
  }, []);

  const setRowType = useCallback((index: number, type: CodProofType) => {
    setRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, type } : row))
    );
  }, []);

  const setRowFile = useCallback((index: number, file: File | null) => {
    // A new file clears the previous attempt's outcome — otherwise retrying
    // after a failure keeps showing the old error against the new file.
    setRows((prev) =>
      prev.map((row, i) =>
        i === index
          ? { ...row, file, uploadedUrl: undefined, error: undefined }
          : row
      )
    );
  }, []);

  const removeExisting = useCallback((imageUrl: string) => {
    setExisting((prev) => prev.filter((proof) => proof.imageUrl !== imageUrl));
  }, []);

  /**
   * Upload the filled rows and return the full list the form should submit.
   *
   * Sequential, not `Promise.all`: the uploader is a multipart POST per file
   * and firing ten at once is how a hub's connection produces five failures and
   * a half-attached confirmation. A failure throws, which leaves the form open
   * with every row intact — nothing is submitted half-done.
   *
   * Rows with no file are skipped, so a stray `+ Tambah Lampiran` the operator
   * never filled in costs nothing.
   *
   * The outcome is recorded ON EACH ROW as it resolves, which is what lets the
   * list say which files are stored and which one failed. Before, the only
   * signal was a toast naming no file at all — with three attachments and one
   * failure, the operator could not tell which to re-pick.
   */
  const upload = useCallback(async (): Promise<CodConfirmationProof[]> => {
    const pending = rows
      .map((row, index) => ({ row, index }))
      .filter(
        (entry): entry is { row: ProofRow & { file: File }; index: number } =>
          entry.row.file !== null
      );
    if (pending.length === 0) return existing;

    const settle = (index: number, patch: Partial<ProofRow>) =>
      setRows((prev) =>
        prev.map((row, i) => (i === index ? { ...row, ...patch } : row))
      );

    setUploading(true);
    try {
      const uploaded: CodConfirmationProof[] = [];
      for (const { row, index } of pending) {
        // Already stored by an earlier attempt — a retry after one file failed
        // must not re-upload (and re-charge) the ones that succeeded.
        if (row.uploadedUrl) {
          uploaded.push({ imageUrl: row.uploadedUrl, type: row.type });
          continue;
        }
        try {
          const imageUrl = await uploadCodProof(row.file);
          settle(index, { uploadedUrl: imageUrl, error: undefined });
          uploaded.push({ imageUrl, type: row.type });
        } catch (error) {
          settle(index, {
            error: (error as Error).message || 'Gagal mengunggah',
          });
          throw error;
        }
      }
      return [...existing, ...uploaded];
    } finally {
      setUploading(false);
    }
  }, [existing, rows]);

  const count = existing.length + filled.length;

  return useMemo(
    () => ({
      existing,
      rows,
      uploading,
      count,
      isEmpty: count === 0,
      canAddRow: existing.length + rows.length < MAX_ATTACHMENTS,
      addRow,
      removeRow,
      setRowType,
      setRowFile,
      removeExisting,
      upload,
    }),
    [
      existing,
      rows,
      uploading,
      count,
      addRow,
      removeRow,
      setRowType,
      setRowFile,
      removeExisting,
      upload,
    ]
  );
};

export type ProofPicker = ReturnType<typeof useProofPicker>;

/**
 * The attachment field: a Jenis + File pair per attachment, and a button that
 * adds another pair.
 *
 * `required` only changes the label and the hint — the submit gate lives in the
 * form, which is the thing that knows whether it can be submitted.
 *
 * `allowRemoveExisting` is off by default: only a correction may prune the
 * stored list, and the add-proof modal is add-only by design.
 */
export const CodProofPicker: React.FC<{
  picker: ProofPicker;
  required?: boolean;
  allowRemoveExisting?: boolean;
  label?: string;
  hint?: React.ReactNode;
}> = ({
  picker,
  required = false,
  allowRemoveExisting = false,
  label = 'Bukti & Lampiran',
  hint,
}) => (
  <div className="flex flex-col gap-2.5">
    <div className="flex items-center gap-2">
      <Label>{label}</Label>
      <span
        className={`rounded px-1.5 py-0.5 text-[11px] ${
          required
            ? 'bg-state-error-lighter text-state-error-dark'
            : 'bg-bg-weak-50 text-text-sub-600'
        }`}
      >
        {required ? 'Wajib' : 'Opsional'}
      </span>
    </div>

    {picker.existing.length > 0 && (
      <div className="flex flex-col gap-1.5">
        {picker.existing.map((proof) => (
          <div
            key={proof.imageUrl}
            className="flex items-center justify-between gap-3 rounded-lg border border-stroke-soft-200 px-3 py-2"
          >
            <a
              href={proof.imageUrl}
              target="_blank"
              rel="noreferrer"
              className="min-w-0 truncate text-sm text-accent hover:underline"
            >
              {COD_PROOF_TYPE_LABELS[proof.type] ?? proof.type}
            </a>
            {allowRemoveExisting && (
              <Button
                style="stroke"
                tone="neutral"
                size="xs"
                onClick={() => picker.removeExisting(proof.imageUrl)}
              >
                Hapus
              </Button>
            )}
          </div>
        ))}
      </div>
    )}

    {picker.rows.map((row, index) => (
      <div
        key={index}
        className="flex flex-col gap-2 rounded-lg border border-stroke-soft-200 p-3"
      >
        <div className="flex items-end gap-2">
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <Label className="text-xs">Jenis Lampiran</Label>
            <Select
              value={row.type}
              onValueChange={(value) =>
                picker.setRowType(index, value as CodProofType)
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {COD_PROOF_TYPES.map((type) => (
                  <SelectItem key={type} value={type}>
                    {COD_PROOF_TYPE_LABELS[type]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {/* Only offered from the second row on: removing the only row would
              leave the form with nowhere to attach anything. */}
          {picker.rows.length > 1 && (
            <Button
              style="stroke"
              tone="neutral"
              size="xs"
              onClick={() => picker.removeRow(index)}
            >
              Hapus
            </Button>
          )}
        </div>

        {row.file ? (
          row.uploadedUrl || row.error || picker.uploading ? (
            <FileUploadList>
              <FileUploadItem
                name={row.file.name}
                size={row.file.size}
                mime={row.file.type}
                progress={row.uploadedUrl ? 100 : 50}
                status={
                  row.uploadedUrl
                    ? 'complete'
                    : row.error
                      ? 'error'
                      : 'uploading'
                }
                errorMessage={row.error}
                onRemove={() => picker.setRowFile(index, null)}
              />
            </FileUploadList>
          ) : (
            /* Picked, not sent. The design system has three states —
               uploading / complete / error — and none of them is "chosen but
               not yet uploaded", which is where a file spends ALL its time on
               this form: nothing uploads until submit. Borrowing `complete`
               for it put a green tick and the word "Completed" on a file that
               had never left the browser. A plain row tells the truth. */
            <div className="flex items-center gap-2 rounded-lg border border-stroke-soft-200 px-3 py-2.5">
              <i className="ri-attachment-2 text-base text-icon-sub-600" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-text-strong-950">
                  {row.file.name}
                </div>
                <div className="text-xs text-text-sub-600">
                  Siap diunggah saat disimpan
                </div>
              </div>
              <Button
                style="ghost"
                tone="neutral"
                size="xs"
                onClick={() => picker.setRowFile(index, null)}
              >
                Hapus
              </Button>
            </div>
          )
        ) : (
          <FileUploadDropzone
            accept={PROOF_ACCEPTED}
            multiple={false}
            maxBytes={PROOF_MAX_BYTES}
            onFiles={(files) => picker.setRowFile(index, files[0] ?? null)}
            title="Pilih file atau tarik ke sini."
            description="PDF, PNG, JPG atau WebP, maksimal 5 MB."
            browseLabel="Pilih file"
          />
        )}
      </div>
    ))}

    {picker.canAddRow && (
      <Button
        style="stroke"
        tone="neutral"
        size="xs"
        className="self-start"
        onClick={picker.addRow}
      >
        + Tambah Lampiran
      </Button>
    )}

    {hint}
  </div>
);
